import { useEffect, useRef, useState } from "react";
import { Check, X } from "lucide-react";

// Editable transcript preview shown above the chat composer while the
// mic is capturing or after a successful capture. Never auto-sends —
// the student either commits the transcript into the existing composer
// (which they then Send manually) or cancels.
//
// Props:
//   listening   : boolean — controls the "dinleniyor" chip
//   interim     : string  — live partial text (grey italic)
//   transcript  : string  — final accumulated text (editable)
//   onCommit(text) — appends the (possibly edited) transcript to composer
//   onCancel()     — discards the transcript
export function VoicePreview({
  listening = false,
  uploading = false,
  interim = "",
  transcript = "",
  onCommit,
  onCancel,
}) {
  const [draft, setDraft] = useState(transcript);
  const areaRef = useRef(null);

  useEffect(() => {
    // Sync incoming final transcript into the editable draft. We do not
    // overwrite student edits once they've typed anything; only replace
    // while the field is still tracking the raw transcript.
    if (transcript && draft === "") setDraft(transcript);
    if (transcript && draft && !draft.startsWith(transcript) && transcript !== draft) {
      setDraft(transcript);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transcript]);

  const hasAny = draft.trim().length > 0 || transcript.trim().length > 0 || listening || uploading;
  if (!hasAny) return null;

  const commit = () => {
    const text = (draft || transcript || "").trim();
    if (!text) return;
    onCommit?.(text);
    setDraft("");
  };
  const cancel = () => {
    setDraft("");
    onCancel?.();
  };

  return (
    <div
      className="mb-3 rounded-2xl border border-[#1E3A8A]/20 bg-[#1E3A8A]/[0.03] px-3 py-3"
      role="region"
      aria-label="Sesli giriş önizleme"
      data-testid="voice-transcript-preview"
    >
      <div className="flex items-center gap-2 mb-2">
        {listening ? (
          <span
            className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-red-50 border border-red-200 text-[10px] font-semibold uppercase tracking-wider text-red-700"
            data-testid="voice-listening-indicator"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
            Dinleniyor
          </span>
        ) : uploading ? (
          <span
            className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-amber-50 border border-amber-200 text-[10px] font-semibold uppercase tracking-wider text-amber-700"
            data-testid="voice-uploading-indicator"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
            Yazıya çevriliyor
          </span>
        ) : (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-white border border-zinc-200 text-[10px] font-semibold uppercase tracking-wider text-zinc-600">
            Sesli giriş
          </span>
        )}
        <span className="text-[11px] text-zinc-500">
          Metni düzenleyip “Yazıya ekle” diyene kadar hiçbir şey gönderilmez.
        </span>
      </div>

      <textarea
        ref={areaRef}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        rows={2}
        placeholder={listening ? "Konuşmaya başla…" : "Sesten çevrilen metin"}
        className="w-full resize-none rounded-lg border border-zinc-200 bg-white px-3 py-2 text-sm text-zinc-900 focus:outline-none focus:border-[#1E3A8A]/40 focus:ring-2 focus:ring-[#1E3A8A]/10"
        data-testid="voice-transcript-editable"
        aria-label="Sesten çevrilen düzenlenebilir metin"
      />

      {listening && interim && !draft && (
        <div
          className="mt-1 text-xs italic text-zinc-500 truncate"
          data-testid="voice-interim"
          aria-live="polite"
        >
          {interim}
        </div>
      )}

      <div className="mt-2 flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={cancel}
          className="inline-flex items-center gap-1 h-8 px-3 rounded-full bg-white border border-zinc-200 text-xs text-zinc-700 hover:border-zinc-300"
          data-testid="voice-cancel"
        >
          <X className="w-3 h-3" /> İptal
        </button>
        <button
          type="button"
          onClick={commit}
          disabled={!draft.trim() && !transcript.trim()}
          className="inline-flex items-center gap-1 h-8 px-3 rounded-full bg-[#1E3A8A] text-white text-xs hover:bg-[#1E40AF] disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="voice-commit"
        >
          <Check className="w-3 h-3" /> Yazıya ekle
        </button>
      </div>
    </div>
  );
}
